import DOMPurify from "dompurify";

import {Message} from 'shared/message';
import {Sex} from "shared/user";
import {MESSAGES_ALLOWED_TAGS} from "shared/utils";
import {LocalModeratorPermissions, ModeratorInfo, RoomType, ServerRoom} from "shared/room";

export class MessageWrapper extends Message {

    public progress: boolean = false;
    public progressCount: number = 0;

    constructor(private realMessage: Message, public state: MessageStates = MessageStates.sending, readonly service: boolean = false) {
        
        let message = DOMPurify.sanitize(realMessage.message, {ALLOWED_TAGS: MESSAGES_ALLOWED_TAGS})

        message = message.replace(/(^|\s)(@.+)( ?→)/igm, (it, group1, group2, group3) => {
            return `${group1}<span class="appealed-login">${group2.trim()}</span>${group3}`
        })

        super(realMessage.id, message, realMessage.sent, realMessage.from, realMessage.type, realMessage.pinned);
    }

    getRealMessage(): Message {
        return new Message(this.id, this.message, this.sent, this.from, this.type, this.pinned);
    }
}

export type RoomId = string

export class OpenRoom {
    constructor(readonly title: string,
                readonly fromServer: boolean,
                readonly type: RoomType,
                readonly disconnected: boolean,
                readonly unreadMessages: number,
                readonly closable: boolean,
                readonly maxMessagesCount = 100,
                readonly roomId: RoomId = `${title}-${type}`) //Необходимо только для того, чтоб различать приватные комнаты и комнаты с сервера
    {
    }
}

export class ChatInfo extends OpenRoom {
    /**
     * Конструктор
     * @param title - название чата
     * @param isFromServer - true если это чат с сервера, false если это личная переписка с кем то
     * @param type - тип комнаты (Лоби, админка, баны ...)
     * @param createdBy - логин того кто создал комнату
     * @param allowedGenders - кто может входить в комнату
     * @param isPassword - true если у чата установлен пароль
     * @param isPaid - false если чат неоплачен владельцем, актуально только для платных комнат
     * @param allowOnlyRegistered - разрешены ли только зарегистрированные или нет
     * @param pinnedMessage - закрепленное сообщение
     * @param moderators - список модераторов с их правами
     * @param closable - true если чат можно закрыть
     * @param maxMessagesCount - максимальное количество сообщений в чате
     */
    constructor(title: string, isFromServer: boolean, type: RoomType, readonly createdBy: string, public readonly allowedGenders: Array<Sex>, public readonly isPassword: boolean, readonly isPaid: boolean, readonly allowOnlyRegistered: boolean, public pinnedMessage: string, public moderators: Array<ModeratorInfo>, closable = !isFromServer, maxMessagesCount: number) {
        super(title, isFromServer, type, false, 0, closable, maxMessagesCount);
    }

    // Не стоит сюда добавлять методы так как используется копирование
}

export enum MessageStates {
    /**
     * Отправлено
     */
    sent = 'sent',
    /**
     * отправляется
     */
    sending = 'sending',
    /**
     * Ошибка при отправке
     */
    error = 'error'
}

//TODO Максимально тупо так как есть RoomUtils в которой тот же код и те же методы
export class ChatInfoHelper {

    static isUserHasPermission(room: ChatInfo, login: string, permission: LocalModeratorPermissions): boolean {
        if(!room) return false

        if (room.type !== RoomType.PAID_ROOM) return false

        //Создателю разрешено все
        if (room.createdBy === login) return true

        const info = ChatInfoHelper.moderatorInfo(room, login)

        if (!info) return false

        if(info && permission === LocalModeratorPermissions.BASE_PERMISSION) {
            return true
        }

        return info.permissions.includes(permission)
    }

    static moderatorInfo(room: ChatInfo, login: string): ModeratorInfo | undefined {
        if(!room) return undefined

        if (!room) return undefined

        if (room.type !== RoomType.PAID_ROOM) return undefined;

        if (room.createdBy === login) {
            //У создателя комнаты все права
            return {
                login: login,
                permissions: [
                    LocalModeratorPermissions.BASE_PERMISSION,
                    LocalModeratorPermissions.ADD_REMOVE_MODERATORS,
                    LocalModeratorPermissions.EDIT_PINNED_MESSAGE,
                    LocalModeratorPermissions.EDIT_BAN_REASONS,
                ]
            }
        }

        return room.moderators.find((m) => m.login === login);
    }

    static hasNonBasePermission(room: ChatInfo, login: string,): boolean {
        return ChatInfoHelper.moderatorInfo(room, login)?.permissions.filter(p => p !== LocalModeratorPermissions.BASE_PERMISSION).length > 0
    }
}
